import { ApprovalableTypeEnum } from 'api-hooks/common';
import { PaymentMethodEnum } from 'api-hooks/payment-channel';
import {
  getRefundKey,
  getRefundsKey,
  RefundModel,
  RefundMutationInput,
  ReleasePurchaseInput,
  ReleaseSaleInput,
  useCancelRefund,
} from 'api-hooks/refund/refund';
import { AuthorizationRules } from 'common/constants';
import { RefundStatusEnum } from 'common/constants/enum';
import formSetErrors from 'common/helpers/form-setError';
import { queryClient } from 'common/repositories/query-client';
import HOCInput from 'components/common/hoc-input';
import notification from 'components/common/notification';
import Separator from 'components/common/separator';
import { Form } from 'components/elements';
import FormHeader from 'components/widgets/form-header';
import { useAuthorization } from 'hooks/use-authorization';
import useDialog from 'hooks/use-dialog';
import useYupValidationResolver from 'hooks/use-yup-validation-resolver';
import RequestApprovalButton from 'legacy-modules/approval/approval/components/request-approval-button';
import RequestApprovalModal from 'legacy-modules/approval/approval/components/request-modal';
import ResolveApprovalButton from 'legacy-modules/common/resolve-approval';
import { SeparatorVertical } from 'legacy-modules/styles';
import useTranslation from 'next-translate/useTranslation';
import * as React from 'react';
import { useForm } from 'react-hook-form';

import { FormType, refundSchema } from './form-type';
import InformationField from './information-field';
import ActivityPurchaseSection from './refund-agent/activity';
import AgentDetailField from './refund-agent/agent-detail-field';
import AgentPaymentField from './refund-agent/agent-payment-field';
import CruisePurchaseSection from './refund-agent/cruise';
import DocumentPurchaseSection from './refund-agent/document';
import FlightPurchaseSection from './refund-agent/flight';
import HotelPurchaseSection from './refund-agent/hotel';
import InformationAgentField from './refund-agent/information-agent-field';
import ActivitySaleSection from './refund-customer/activity';
import CruiseSaleSection from './refund-customer/cruise';
import CustomerDetailField from './refund-customer/customer-detail-field';
import CustomerPaymentField from './refund-customer/customer-payment-field';
import DocumentSaleSection from './refund-customer/document';
import FlightSaleSection from './refund-customer/flight';
import HotelSaleSection from './refund-customer/hotel';
import InformationCustomerField from './refund-customer/information-customer-field';
import ProductSaleSection from './refund-customer/product';
import TourSaleSection from './refund-customer/tour';
import { filteringReleaseSaleItems } from './refund-customer/utils';
import { FormDivider } from './styles';
import useGetDefaultValues from '../hooks/use-get-default-values';

export type SaleInvoiceRefundType = {
  id: string;
  invoiceNumber: string;
  customerId: string;
  customerBillingId: string | null;
  code: string | null;
  description: string;
  transactionAt: Date;
  total: number;
  refId: string;
  adminId: string;
  operatorId: string | undefined;
  salesmanId: string | undefined;
  transactionSaleType: string;
  label?: string;
  tourId?: string | undefined;
};

interface Props {
  refund?: RefundModel;
  onSubmit: (
    input: RefundMutationInput,
    form: ReturnType<typeof useForm>,
  ) => Promise<RefundModel | undefined>;
  saleInvoice?: SaleInvoiceRefundType;
}

export default function RefundForm(props: Props) {
  const { refund, saleInvoice } = props;
  const { t } = useTranslation();
  const dialog = useDialog();
  const { can } = useAuthorization();
  const { mutateAsync: cancelMutate, isLoading: cancelLoading } =
    useCancelRefund();

  const defaultValues = useGetDefaultValues({
    refund,
    saleInvoice,
  });

  const resolver = useYupValidationResolver(refundSchema());

  const methods = useForm({
    defaultValues,
    resolver,
    mode: 'all',
  });

  const onSubmit = React.useCallback(
    async (values: FormType) => {
      try {
        const {
          activityPurchases,
          activitySales,
          cruisePurchases,
          cruiseSales,
          documentPurchases,
          documentSales,
          flightPurchases,
          flightSales,
          hotelPurchases,
          hotelSales,
          ...rest
        } = values;

        const { saleItems, saleAddOns, purchaseAddOns, purchaseItems } =
          filteringReleaseSaleItems(values);

        const releaseSale: ReleaseSaleInput = {
          addOns: saleAddOns,
          items: saleItems,
          ...values.releaseSale,
          paymentableId:
            values.releaseSale.paymentMethod === PaymentMethodEnum.Balance
              ? values.releaseSale.customerId
              : values.releaseSale.paymentableId,
        };

        const releasePurchase: ReleasePurchaseInput | null =
          values.releasePurchase
            ? {
                addOns: purchaseAddOns,
                items: purchaseItems,
                ...values.releasePurchase,
                paymentableId:
                  values.releasePurchase.paymentMethod ===
                  PaymentMethodEnum.Balance
                    ? values.releasePurchase.agentId
                    : values.releasePurchase.paymentableId,
              }
            : null;

        await props.onSubmit(
          { ...rest, releaseSale, releasePurchase },
          methods as any,
        );
      } catch (e) {
        if (e.errors) {
          formSetErrors(e.errors, methods.setError);
        }
        notification.error({ message: e.message });
      }
    },
    [methods, props],
  );

  const onCancel = React.useCallback(async () => {
    dialog.showCustom({
      render: (onClose) => (
        <RequestApprovalModal
          onClose={onClose}
          data={{
            approvalableId: refund!.id!,
            approvalableType: ApprovalableTypeEnum.Refund,
          }}
          onSubmit={async ({ createApproval, form, input }) => {
            try {
              await cancelMutate(
                {
                  id: refund!.id!,
                },
                {
                  onSuccess: async (data) => {
                    const result = await createApproval(input);
                    notification.success({
                      message: t('common:successfully_canceled'),
                    });
                    notification.success({
                      message: result.message,
                    });

                    queryClient.refetchQueries([getRefundsKey()[0]]);
                    queryClient.refetchQueries(
                      getRefundKey({ id: refund!.id }),
                    );
                    onClose();
                  },
                },
              );
            } catch (error) {
              notification.error({
                message: error.message,
              });
            }
          }}
        />
      ),
    });
  }, [refund, cancelMutate, dialog, t]);

  const onDimissApproval = () => {
    queryClient.refetchQueries([getRefundsKey()[0]]);
    queryClient.refetchQueries(getRefundKey({ id: refund!.id }));
  };

  const isRejected =
    refund?.status.includes('rejected') &&
    can(AuthorizationRules.RefundsResolve);

  const RenderRightComponent = () => {
    if (refund) {
      return (
        <>
          <Separator gap={16} />
          {(() => {
            if (
              refund?.status === RefundStatusEnum.DraftCancel ||
              refund?.status === RefundStatusEnum.Draft
            ) {
              return (
                <RequestApprovalButton
                  data={{
                    approvalableId: refund.id,
                    approvalableType: ApprovalableTypeEnum.Refund,
                  }}
                  onDismiss={onDimissApproval}
                />
              );
            }
            if (isRejected && can(AuthorizationRules.RefundsResolve)) {
              return (
                <ResolveApprovalButton
                  isActionComponent={false}
                  type="refunds"
                  id={refund.id}
                  onSuccess={() => {
                    queryClient.refetchQueries(['get-refunds']);
                    queryClient.refetchQueries(getRefundKey({ id: refund.id }));
                  }}
                />
              );
            }
            return <></>;
          })()}
        </>
      );
    }
    return null;
  };

  const customViewName =
    refund && [t('common:refund'), refund?.number].join(' - ');

  return (
    <Form
      methods={methods}
      onSubmit={onSubmit}
      defaultEditable={!refund}
      customViewName={customViewName}
      noReset
    >
      <FormHeader
        data={refund}
        deleteText={t('common:cancel')}
        onDelete={onCancel}
        noDelete
        title={
          refund
            ? customViewName
            : t('common:add_extra', { extra: t('common:refund') })
        }
        isDeleteLoading={cancelLoading}
        noEdit={
          refund?.status === RefundStatusEnum.DraftCancel ||
          refund?.status === RefundStatusEnum.Draft ||
          !can(AuthorizationRules.RefundsUpdate)
        }
        onRenderRightComponent={<RenderRightComponent />}
      />
      <div>
        <InformationField />
        <InformationCustomerField />
        <CustomerPaymentField />
        <CustomerDetailField />
        <FlightSaleSection />
        <HotelSaleSection />
        <CruiseSaleSection />
        <ActivitySaleSection />
        <DocumentSaleSection />
        <TourSaleSection />
        <ProductSaleSection />
        <SeparatorVertical $margin={36} />
        <InformationAgentField />
        <HOCInput keys={['isHideAgent']}>
          {({ isHideAgent }) => (
            <>
              {!isHideAgent && (
                <>
                  <AgentPaymentField />
                  <AgentDetailField />
                  <FlightPurchaseSection />
                  <HotelPurchaseSection />
                  <CruisePurchaseSection />
                  <ActivityPurchaseSection />
                  <DocumentPurchaseSection />
                </>
              )}
            </>
          )}
        </HOCInput>
        <FormDivider />
      </div>
    </Form>
  );
}
